
import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Card from '../../components/base/Card';
import { supabaseService } from '../../utils/supabase';

interface PaperItem {
  id: string;
  title: string;
  authors: string;
  journal: string;
  year: string;
  abstract: string;
  keywords: string[];
  url?: string;
  image?: string;
}

export default function Papers() {
  const [papersData, setPapersData] = useState<PaperItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPapersData();
  }, []);

  const loadPapersData = async () => {
    try {
      const data = await supabaseService.getAllData();
      if (data.papers && data.papers.length > 0) {
        setPapersData(data.papers);
      }
    } catch (error) {
      console.error('加载论文数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-white to-slate-200">
      <Navbar />
      
      <div className="pt-20 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">All Publications</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Prepare yourself
            </p>
          </div>
          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">加载中...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && (!papersData || papersData.length === 0) && (
            <div className="text-center py-12">
              <i className="ri-file-text-line text-6xl text-gray-400 mb-4"></i>
              <h3 className="text-xl font-medium text-gray-600 mb-2">暂无论文</h3>
              <p className="text-gray-500">请通过管理后台添加论文</p>
            </div>
          )}

          {/* Papers List */}
          {!loading && papersData && papersData.length > 0 && (
            <div className="space-y-8">
              {papersData.map((paper) => (
              <Card key={paper.id} className="p-6 hover:shadow-lg transition-shadow" glass>
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Paper Info */}
                  <div className="flex-1">
                    <div className="flex items-start space-x-4 mb-4">
                      {/* Timeline dot */}
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                        <i className="ri-file-text-line text-white text-lg"></i>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">{paper.title}</h3>
                            <p className="text-blue-600 font-medium">发表于《{paper.journal}》• {paper.year}</p>
                          </div>
                          {paper.url && (
                            <div className="mt-2 md:mt-0">
                              <a 
                                href={paper.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                                title="查看论文全文"
                              >
                                <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          )}
                        </div>
                        <p className="text-gray-600 text-sm">作者：{paper.authors}</p>
                      </div>
                    </div>
                    
                    <div className="ml-16">
                      <div className="mb-4">
                        <h4 className="font-semibold text-gray-900 mb-2">摘要</h4>
                        <p className="text-gray-700 leading-relaxed">{paper.abstract}</p>
                      </div>
                      
                      {paper.keywords && paper.keywords.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">关键词</h4>
                          <div className="flex flex-wrap gap-2">
                            {paper.keywords.map((keyword, keywordIndex) => (
                              <span 
                                key={keywordIndex}
                                className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium border border-blue-200"
                              >
                                {keyword}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Paper Image */}
                  {paper.image && (
                    <div className="lg:w-64 flex-shrink-0">
                      <img 
                        src={paper.image} 
                        alt={paper.title}
                        className="w-full h-48 lg:h-full object-cover object-top rounded-lg border shadow-sm"
                      />
                    </div>
                  )}
                </div>
              </Card>
            ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
